import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, Plus, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react'
import { useState, useCallback } from 'react'

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import { Switch } from '@/common/ui/switch'
import { Skeleton } from '@/common/ui/skeleton'
import { Badge } from '@/common/ui/badge'
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/common/ui/accordion'

import { ChatMessageType } from '@/schema'
import {
	useQueryChatSettings,
	useMutationUpdateChatSettings,
} from '../../../api/hooks'
import { autoReplySettingsSchema, MAX_AUTO_REPLY_RULES } from '../types'
import type {
	AutoReplySettingsFormData,
	AutoReplyRuleInput,
	MessageContentInput,
} from '../types'
import { MessageListEditor } from './MessageListEditor'
import { MessageListPreview } from './MessagePreview'

export function AutoReplySettingsPage() {
	const { data: settings, isLoading } = useQueryChatSettings()
	const updateMutation = useMutationUpdateChatSettings()
	const [previewRuleIndex, setPreviewRuleIndex] = useState<number | null>(null)

	const form = useForm<AutoReplySettingsFormData>({
		resolver: zodResolver(autoReplySettingsSchema),
		defaultValues: {
			autoReplyEnabled: false,
			autoReplyRules: [],
		},
		values: settings
			? {
					autoReplyEnabled: settings.autoReplyEnabled,
					autoReplyRules: transformRulesResponseToInput(
						settings.autoReplyRules,
					),
				}
			: undefined,
	})

	const autoReplyEnabled = form.watch('autoReplyEnabled')
	const autoReplyRules = form.watch('autoReplyRules')

	const handleSubmit = (data: AutoReplySettingsFormData) => {
		updateMutation.mutate({
			autoReplyEnabled: data.autoReplyEnabled,
			autoReplyRules: data.autoReplyRules.map((rule, ruleIndex) => ({
				keywords: rule.keywords,
				priority: ruleIndex,
				isActive: rule.isActive,
				messages: rule.messages.map((msg, msgIndex) => ({
					messageType: msg.messageType,
					order: msgIndex,
					...(msg.text && { text: msg.text }),
					...(msg.stickerPackageId && {
						stickerPackageId: msg.stickerPackageId,
					}),
					...(msg.stickerId && { stickerId: msg.stickerId }),
					...(msg.imageUrl && { imageUrl: msg.imageUrl }),
					...(msg.videoUrl && { videoUrl: msg.videoUrl }),
					...(msg.videoPreviewUrl && { videoPreviewUrl: msg.videoPreviewUrl }),
					...(msg.audioUrl && { audioUrl: msg.audioUrl }),
					...(msg.audioDuration !== undefined && {
						audioDuration: msg.audioDuration,
					}),
					...(msg.fileUrl && { fileUrl: msg.fileUrl }),
					...(msg.fileName && { fileName: msg.fileName }),
					...(msg.locationTitle && { locationTitle: msg.locationTitle }),
					...(msg.locationAddress && { locationAddress: msg.locationAddress }),
					...(msg.locationLatitude !== undefined && {
						locationLatitude: msg.locationLatitude,
					}),
					...(msg.locationLongitude !== undefined && {
						locationLongitude: msg.locationLongitude,
					}),
					...(msg.flexAltText && { flexAltText: msg.flexAltText }),
					...(msg.flexContents && {
						flexContents: msg.flexContents as Record<string, never>,
					}),
				})),
			})),
		})
	}

	const addAutoReplyRule = useCallback(() => {
		const currentRules = form.getValues('autoReplyRules')
		if (currentRules.length >= MAX_AUTO_REPLY_RULES) return

		const newRule: AutoReplyRuleInput = {
			id: crypto.randomUUID(),
			keywords: [],
			priority: currentRules.length,
			isActive: true,
			messages: [
				{
					id: crypto.randomUUID(),
					order: 0,
					messageType: ChatMessageType.TEXT,
				},
			],
		}

		form.setValue('autoReplyRules', [...currentRules, newRule])
	}, [form])

	const removeAutoReplyRule = useCallback(
		(index: number) => {
			const currentRules = form.getValues('autoReplyRules')
			form.setValue(
				'autoReplyRules',
				currentRules
					.filter((_, i) => i !== index)
					.map((rule, i) => ({ ...rule, priority: i })),
			)
			if (previewRuleIndex === index) {
				setPreviewRuleIndex(null)
			}
		},
		[form, previewRuleIndex],
	)

	const togglePreview = useCallback(
		(index: number) => {
			setPreviewRuleIndex(previewRuleIndex === index ? null : index)
		},
		[previewRuleIndex],
	)

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>การตอบกลับอัตโนมัติ</CardTitle>
						<CardDescription>
							ตั้งค่าการตอบกลับอัตโนมัติตามคำสำคัญที่ลูกค้าส่งมา
							(รองรับหลายข้อความต่อกฎ)
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<FormField
							control={form.control}
							name="autoReplyEnabled"
							render={({ field }) => (
								<FormItem className="flex items-center justify-between rounded-lg border p-4">
									<div className="space-y-0.5">
										<FormLabel className="text-base">
											เปิดใช้งานการตอบกลับอัตโนมัติ
										</FormLabel>
										<FormDescription>
											ตอบกลับอัตโนมัติเมื่อข้อความตรงกับคำสำคัญที่กำหนด
										</FormDescription>
									</div>
									<FormControl>
										<Switch
											checked={field.value}
											onCheckedChange={field.onChange}
										/>
									</FormControl>
								</FormItem>
							)}
						/>

						{autoReplyEnabled && (
							<div className="space-y-4">
								<div className="flex items-center justify-between">
									<h4 className="text-sm font-medium">
										กฎการตอบกลับ ({autoReplyRules.length}/{MAX_AUTO_REPLY_RULES}
										)
									</h4>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={addAutoReplyRule}
										disabled={autoReplyRules.length >= MAX_AUTO_REPLY_RULES}
									>
										<Plus className="mr-2 h-4 w-4" />
										เพิ่มกฎ
									</Button>
								</div>

								{autoReplyRules.length === 0 ? (
									<div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
										ยังไม่มีกฎการตอบกลับ คลิกปุ่มด้านบนเพื่อเพิ่ม
									</div>
								) : (
									<Accordion type="multiple" className="space-y-2">
										{autoReplyRules.map((rule, index) => (
											<AccordionItem
												key={rule.id || index}
												value={`rule-${index}`}
												className="rounded-lg border"
											>
												<AccordionTrigger className="px-4 hover:no-underline">
													<div className="flex flex-1 items-center gap-2">
														<GripVertical className="h-4 w-4 text-muted-foreground" />
														<span className="font-medium">กฎ #{index + 1}</span>
														{rule.keywords.length > 0 && (
															<div className="flex flex-wrap gap-1">
																{rule.keywords.slice(0, 3).map((keyword, i) => (
																	<Badge
																		key={i}
																		variant="secondary"
																		className="text-xs"
																	>
																		{keyword}
																	</Badge>
																))}
																{rule.keywords.length > 3 && (
																	<Badge variant="outline" className="text-xs">
																		+{rule.keywords.length - 3}
																	</Badge>
																)}
															</div>
														)}
														{!rule.isActive && (
															<Badge variant="outline" className="text-xs">
																ปิดใช้งาน
															</Badge>
														)}
														<Badge
															variant="secondary"
															className="ml-auto text-xs"
														>
															{rule.messages.length} ข้อความ
														</Badge>
													</div>
												</AccordionTrigger>
												<AccordionContent className="space-y-4 px-4 pb-4">
													<div className="flex items-center justify-between">
														<FormField
															control={form.control}
															name={`autoReplyRules.${index}.isActive`}
															render={({ field }) => (
																<FormItem className="flex items-center gap-2">
																	<FormControl>
																		<Switch
																			checked={field.value}
																			onCheckedChange={field.onChange}
																		/>
																	</FormControl>
																	<FormLabel className="!mt-0">
																		เปิดใช้งาน
																	</FormLabel>
																</FormItem>
															)}
														/>
														<Button
															type="button"
															variant="outline"
															size="sm"
															onClick={() => togglePreview(index)}
														>
															{previewRuleIndex === index ? (
																<>
																	<EyeOff className="mr-2 h-4 w-4" />
																	ซ่อนตัวอย่าง
																</>
															) : (
																<>
																	<Eye className="mr-2 h-4 w-4" />
																	ดูตัวอย่าง
																</>
															)}
														</Button>
													</div>

													<FormField
														control={form.control}
														name={`autoReplyRules.${index}.keywords`}
														render={({ field }) => (
															<FormItem>
																<FormLabel>คำสำคัญ</FormLabel>
																<FormControl>
																	<Input
																		placeholder="คั่นด้วยเครื่องหมายจุลภาค เช่น สวัสดี, ติดต่อ, สอบถาม"
																		value={field.value.join(', ')}
																		onChange={(e) =>
																			field.onChange(
																				e.target.value
																					.split(',')
																					.map((k) => k.trim())
																					.filter(Boolean),
																			)
																		}
																	/>
																</FormControl>
																<FormDescription>
																	ระบบจะตอบกลับเมื่อข้อความมีคำเหล่านี้
																</FormDescription>
																<FormMessage />
															</FormItem>
														)}
													/>

													<div className="grid gap-6 lg:grid-cols-2">
														<div className="space-y-4">
															<FormLabel>ข้อความตอบกลับ</FormLabel>
															<FormField
																control={form.control}
																name={`autoReplyRules.${index}.messages`}
																render={({ field }) => (
																	<FormItem>
																		<FormControl>
																			<MessageListEditor
																				messages={field.value}
																				onChange={field.onChange}
																				maxMessages={5}
																			/>
																		</FormControl>
																	</FormItem>
																)}
															/>
														</div>

														{previewRuleIndex === index && (
															<div className="space-y-4">
																<FormLabel>ตัวอย่างข้อความ</FormLabel>
																<MessageListPreview messages={rule.messages} />
															</div>
														)}
													</div>

													<div className="flex justify-end">
														<Button
															type="button"
															variant="destructive"
															size="sm"
															onClick={() => removeAutoReplyRule(index)}
														>
															<Trash2 className="mr-2 h-4 w-4" />
															ลบกฎนี้
														</Button>
													</div>
												</AccordionContent>
											</AccordionItem>
										))}
									</Accordion>
								)}
							</div>
						)}
					</CardContent>
				</Card>

				<div className="flex justify-end">
					<Button type="submit" disabled={updateMutation.isPending}>
						<Save className="mr-2 h-4 w-4" />
						บันทึกการตั้งค่า
					</Button>
				</div>
			</form>
		</Form>
	)
}

// ==================== Helpers ====================

function transformRulesResponseToInput(
	rules: Array<{
		id: string
		keywords: string[]
		priority: number
		isActive: boolean
		messages: Array<{
			id: string
			order: number
			messageType: string
			text?: string
			stickerPackageId?: string
			stickerId?: string
			imageUrl?: string
			videoUrl?: string
			videoPreviewUrl?: string
			audioUrl?: string
			audioDuration?: number
			fileUrl?: string
			fileName?: string
			locationTitle?: string
			locationAddress?: string
			locationLatitude?: number
			locationLongitude?: number
			flexAltText?: string
			flexContents?: Record<string, unknown>
		}>
	}>,
): AutoReplyRuleInput[] {
	return rules
		.sort((a, b) => a.priority - b.priority)
		.map((rule) => ({
			id: rule.id,
			keywords: rule.keywords,
			priority: rule.priority,
			isActive: rule.isActive,
			messages: rule.messages
				.sort((a, b) => a.order - b.order)
				.map(
					(msg): MessageContentInput => ({
						id: msg.id,
						order: msg.order,
						messageType: msg.messageType as ChatMessageType,
						text: msg.text,
						stickerPackageId: msg.stickerPackageId,
						stickerId: msg.stickerId,
						imageUrl: msg.imageUrl,
						videoUrl: msg.videoUrl,
						videoPreviewUrl: msg.videoPreviewUrl,
						audioUrl: msg.audioUrl,
						audioDuration: msg.audioDuration,
						fileUrl: msg.fileUrl,
						fileName: msg.fileName,
						locationTitle: msg.locationTitle,
						locationAddress: msg.locationAddress,
						locationLatitude: msg.locationLatitude,
						locationLongitude: msg.locationLongitude,
						flexAltText: msg.flexAltText,
						flexContents: msg.flexContents,
					}),
				),
		}))
}
